import {
    BlockStack,
    Box,
    Button,
    Card,
    EmptySearchResult,
    IndexTable,
    InlineGrid,
    InlineStack,
    Page,
    Text,
} from '@shopify/polaris';
import React, { useState } from 'react';
import usePosApi from '../hooks/Api';
import { useNavigate } from 'react-router-dom';
import { ArrowRightIcon, DeleteIcon, PlusIcon } from '@shopify/polaris-icons';

const ScanReceipts = () => {
    const { request } = usePosApi();
    const navigate = useNavigate();
    const [orders, setOrders] = useState([]);
    const [loading, setLoading] = useState(false);
    const [locationConfig, setLocationConfig] = useState({ allow_consolidation: true });

    React.useEffect(() => {
        const loadLocationConfig = async () => {
            try {
                const { data } = await request('config');
                setLocationConfig(data);
            } catch (error) {
                console.error('Failed to load location config:', error);
            }
        };
        loadLocationConfig();
    }, []);

    const scan = async () => {
        try {
            const payload = await shopify.scanner.capture();
            fetchOrder(payload.data);
        } catch (error) {
            console.log('Scanner Error', error);
        }
    };

    const fetchOrder = (data) => {
        let identifier, type;

        if (data.includes('https://')) {
            identifier = data.split('?receipt=').pop();
            type = 'source';
        } else if (data.includes('R:')) {
            identifier = data.split(':').pop();
            type = 'id';
        } else {
            shopify.toast.show('The barcode could not be read');
            return;
        }

        setLoading(true);
        request(`orders/find?type=${type}&id=${identifier}`)
            .then((response) => {
                if (orders.map((order) => order.order_id).some((id) => id === response.data.order_id)) {
                    shopify.toast.show('The order has already been scanned.');
                    return;
                }
                if (response.data.completed) {
                    shopify.toast.show('There is a completed tax free form for this order.');
                    return;
                }

                // Check if consolidation is disabled and there's already an order
                if (!locationConfig.allow_consolidation && orders.length > 0) {
                    shopify.toast.show('Order consolidation is disabled. Please process the current order first.');
                    return;
                }

                setOrders([...orders, response.data]);
            })
            .finally(() => {
                setLoading(false);
            });
    };

    const deleteOrder = (orderId) => {
        setOrders(orders.filter((order) => order.order_id !== orderId));
    };

    const emptyStateMarkup = (
        <EmptySearchResult
            title="No receipts scanned yet"
            description="Scan some receipts to process the tax free refunds."
        />
    );

    const processOrders = () => {
        const ids = orders.map((order) => order.order_id.split('/').pop());
        navigate(`/orders/form?ids=${ids.join(',')}&source=scan`);
    };

    return (
        <Page narrowWidth title="Scan Receipts" backAction={{ content: 'Index', onAction: () => navigate(-1) }}>
            <Card roundedAbove="sm">
                <BlockStack gap="200">
                    <InlineGrid columns="1fr auto">
                        <Text as="h2" variant="headingSm">
                            Scanned Orders
                        </Text>
                        <Button variant="primary" onClick={scan} accessibilityLabel="Scan Receipt" icon={PlusIcon}>
                            Scan Receipt
                        </Button>
                    </InlineGrid>
                    <IndexTable
                        resourceName={{ singular: 'order', plural: 'orders' }}
                        loading={loading}
                        headings={[
                            {
                                title: 'Name',
                                alignment: 'start',
                            },
                            {
                                title: 'Actions',
                                alignment: 'end',
                            },
                        ]}
                        itemCount={orders.length}
                        selectable={false}
                        emptyState={emptyStateMarkup}
                    >
                        {orders.map((order) => (
                            <IndexTable.Row id={order.order_id} key={order.order_id}>
                                <IndexTable.Cell>{order.name}</IndexTable.Cell>
                                <IndexTable.Cell>
                                    <InlineStack align="end">
                                        <Button
                                            icon={DeleteIcon}
                                            variant="tertiary"
                                            onClick={() => deleteOrder(order.order_id)}
                                        >
                                            Remove
                                        </Button>
                                    </InlineStack>
                                </IndexTable.Cell>
                            </IndexTable.Row>
                        ))}
                    </IndexTable>
                    {orders.length > 0 && (
                        <Button variant="primary" onClick={processOrders} icon={ArrowRightIcon}>
                            Process Orders
                        </Button>
                    )}
                </BlockStack>
            </Card>
        </Page>
    );
};

export default ScanReceipts;
